import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminClient, apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import CoachTrainingTab from "@/components/CoachTrainingTab";
import AdminTournamentsTab from "@/components/tournaments/AdminTournamentsTab";
import AdminCourtsTab from "@/components/AdminCourtsTab";
import AdminSlotManagement from "@/components/AdminSlotManagement";
import TestEmailDialog from "@/components/TestEmailDialog";
import {
  LogOut,
  Mail,
  Trash2,
  ShieldAlert,
  CalendarCheck,
  CalendarClock,
  Ban,
  Lock,
  Loader2,
  PlusCircle,
  User,
} from "lucide-react";

import { useSeo } from "@/lib/seo";
export default function AdminPage() {
  useSeo({ title: "Admin", path: "/admin", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("sandlab_admin_token"));
  const [username, setUsername] = useState(
    localStorage.getItem("sandlab_admin_username")
  );

  if (!token) {
    return (
      <AdminLogin
        onLogin={(t, u) => {
          setToken(t);
          setUsername(u);
        }}
      />
    );
  }
  return (
    <AdminDashboard
      username={username}
      onLogout={() => {
        localStorage.removeItem("sandlab_admin_token");
        localStorage.removeItem("sandlab_admin_username");
        setToken(null);
      }}
    />
  );
}

// ============ Login ============
export function AdminLogin({ onLogin, subtitle }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post(`/admin/login`, form);
      localStorage.setItem("sandlab_admin_token", data.token);
      localStorage.setItem("sandlab_admin_username", data.username);
      onLogin(data.token, data.username);
      toast.success(`Welcome, ${data.username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand/40 px-6 py-16">
      <div className="absolute top-6 left-6">
        <button
          onClick={() => navigate("/academy")}
          data-testid="admin-back-home"
          className="flex items-center gap-2 text-slate-700 font-semibold hover:text-ocean"
        >
          <img
            src="/logo.png"
            alt="The Sand Lab"
            className="h-10 w-auto"
          />
        </button>
      </div>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-soft border border-slate-100 p-8 sm:p-10">
        <div className="flex justify-center mb-4">
          <div className="h-14 w-14 rounded-2xl bg-ocean text-white flex items-center justify-center">
            <Lock className="h-7 w-7" />
          </div>
        </div>
        <h1 className="font-heading font-black text-3xl text-slate-900 text-center">
          Admin Login
        </h1>
        <p className="text-slate-500 text-center mt-2 text-sm">
          {subtitle || "Restricted area · Court managers only"}
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="mt-8 space-y-4"
        >
          <div>
            <Label
              htmlFor="username"
              className="text-xs font-semibold uppercase tracking-widest text-slate-500"
            >
              Username
            </Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="username"
              placeholder="admin1"
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
            />
          </div>
          <div>
            <Label
              htmlFor="password"
              className="text-xs font-semibold uppercase tracking-widest text-slate-500"
            >
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="current-password"
              placeholder="••••••"
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
            />
          </div>

          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full h-12 rounded-full bg-ocean hover:bg-ocean-hover text-white font-bold mt-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ============ Dashboard ============
function AdminDashboard({ username, onLogout }) {
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null); // { time }
  const [blockReason, setBlockReason] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const navigate = useNavigate();

  const dateStr = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);

  const fetchAll = async () => {
    try {
      const [s, r, b] = await Promise.all([
        adminClient.get("/courts/admin/stats"),
        adminClient.get("/admin/reservations"),
        adminClient.get("/admin/blocked-slots"),
      ]);
      setStats(s.data);
      setReservations(r.data || []);
      setBlocked(b.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
        return;
      }
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (d) => {
    setSlotsLoading(true);
    try {
      const { data } = await apiClient.get(`/availability`, {
        params: { date: d },
      });
      setSlots(data.slots);
    } catch {
      toast.error("Could not load slots");
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchSlots(dateStr);
  }, [dateStr]);

  const refreshAll = () => {
    fetchAll();
    fetchSlots(dateStr);
  };

  const handleDelete = async (id) => {
    try {
      await adminClient.delete(`/admin/reservations/${id}`);
      toast.success("Reservation deleted");
      refreshAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleBlock = async (time, reason) => {
    try {
      await adminClient.post(`/admin/block-slot`, {
        date: dateStr,
        time,
        reason: reason || "Blocked by admin",
      });
      toast.success(`Slot ${time} blocked`);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Block failed");
    }
  };

  const openBlockDialog = (time) => {
    setBlockTarget({ time });
    setBlockReason("");
  };

  const confirmBlock = async () => {
    if (!blockTarget) return;
    await handleBlock(blockTarget.time, blockReason.trim());
    setBlockTarget(null);
    setBlockReason("");
  };

  const handleUnblock = async (time) => {
    try {
      await adminClient.delete(`/admin/block-slot`, {
        params: { date: dateStr, time },
      });
      toast.success(`Slot ${time} unblocked`);
      refreshAll();
    } catch {
      toast.error("Unblock failed");
    }
  };

  const handleAdminReserve = async (time) => {
    try {
      await adminClient.post(`/admin/reservations`, {
        full_name: "Admin reservation",
        email: "admin@thesandlab.local",
        phone: "-",
        date: dateStr,
        time,
      });
      toast.success(`Slot ${time} reserved by admin`);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reserve failed");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-sunset font-semibold mb-2">
              Admin panel · {username}
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-slate-900">
              Court Dashboard
            </h1>
            <p className="text-slate-500 mt-2">
              Manage reservations, block slots and oversee all bookings.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setTestEmailOpen(true)}
              data-testid="admin-test-email-btn"
              className="rounded-full border-sunset text-sunset hover:bg-sunset/10"
            >
              <Mail className="h-4 w-4 mr-2" /> Test email
            </Button>
            <Button
              variant="outline"
              onClick={() => setProfileOpen(true)}
              data-testid="admin-profile-btn"
              className="rounded-full border-ocean text-ocean hover:bg-ocean/10"
            >
              <User className="h-4 w-4 mr-2" /> Profile
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/academy")}
              data-testid="admin-go-home"
              className="rounded-full"
            >
              Public site
            </Button>
            <Button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            label="Total reservations"
            value={loading ? "—" : stats?.total_reservations ?? 0}
            icon={<CalendarCheck className="h-5 w-5" />}
            color="bg-ocean"
            testid="stat-total"
          />
          <StatCard
            label="Today's bookings"
            value={loading ? "—" : stats?.today ?? 0}
            icon={<CalendarClock className="h-5 w-5" />}
            color="bg-sunset"
            testid="stat-today"
          />
          <StatCard
            label="Upcoming"
            value={loading ? "—" : stats?.upcoming ?? 0}
            icon={<CalendarCheck className="h-5 w-5" />}
            color="bg-emerald-600"
            testid="stat-upcoming"
          />
          <StatCard
            label="Blocked slots"
            value={loading ? "—" : stats?.blocked ?? 0}
            icon={<Ban className="h-5 w-5" />}
            color="bg-slate-700"
            testid="stat-blocked"
          />
        </div>

        <Tabs defaultValue="manage" className="space-y-6">
          <TabsList
            data-testid="admin-tabs"
            className="bg-white border border-slate-100 rounded-2xl p-1 shadow-soft"
          >
            <TabsTrigger
              value="manage"
              data-testid="tab-manage"
              className="rounded-xl data-[state=active]:bg-ocean data-[state=active]:text-white"
            >
              Slot management
            </TabsTrigger>
            <TabsTrigger
              value="tournaments"
              data-testid="tab-tournaments"
              className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white"
            >
              Tournaments
            </TabsTrigger>
            <TabsTrigger
              value="courts"
              data-testid="tab-courts"
              className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white"
            >
              Κρατήσεις
            </TabsTrigger>
            {username === "admin2" && (
              <TabsTrigger
                value="training"
                data-testid="tab-training"
                className="rounded-xl data-[state=active]:bg-sunset data-[state=active]:text-white"
              >
                Training
              </TabsTrigger>
            )}
          </TabsList>

          {/* Reservations table */}
          {/* Slot management (court booking schedule) */}
          <TabsContent value="manage">
            <AdminSlotManagement onChange={fetchAll} />
          </TabsContent>

          <TabsContent value="tournaments">
            <AdminTournamentsTab />
          </TabsContent>

          <TabsContent value="courts">
            <AdminCourtsTab />
          </TabsContent>

          {username === "admin2" && (
            <TabsContent value="training">
              <CoachTrainingTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Block reason dialog */}
      <Dialog
        open={!!blockTarget}
        onOpenChange={(o) => {
          if (!o) {
            setBlockTarget(null);
            setBlockReason("");
          }
        }}
      >
        <DialogContent data-testid="block-dialog" className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
              <Ban className="h-7 w-7 text-slate-700" />
            </div>
            <DialogTitle className="text-center text-xl font-heading font-black">
              Block slot {blockTarget?.time}
            </DialogTitle>
            <DialogDescription className="text-center">
              Add a reason that will be visible publicly on this slot.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Reason
            </Label>
            <Input
              data-testid="block-reason-input"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="e.g. Training, Tournament, Maintenance"
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
              autoFocus
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {["Training", "Tournament", "Maintenance", "Private event"].map(
                (preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setBlockReason(preset)}
                    data-testid={`block-preset-${preset.toLowerCase().replace(/\s/g, "-")}`}
                    className="px-3 py-1 text-xs rounded-full bg-slate-100 hover:bg-ocean hover:text-white transition-colors text-slate-700 font-semibold"
                  >
                    {preset}
                  </button>
                )
              )}
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBlockTarget(null);
                setBlockReason("");
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="block-confirm-btn"
              onClick={confirmBlock}
              disabled={!blockReason.trim()}
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              Block slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        username={username}
      />
      <TestEmailDialog open={testEmailOpen} onOpenChange={setTestEmailOpen} />
    </div>
  );
}

// ============ Admin Profile Dialog ============
function AdminProfileDialog({ open, onOpenChange, username }) {
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm_password: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await adminClient.post("/admin/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="admin-profile-dialog" className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-ocean/10 flex items-center justify-center mb-2">
            <User className="h-7 w-7 text-ocean" />
          </div>
          <DialogTitle className="text-center text-2xl font-heading font-black">
            Account info
          </DialogTitle>
          <DialogDescription className="text-center">
            Admin · {username}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Current password
            </Label>
            <Input
              type="password"
              data-testid="admin-current-password-input"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              required
              className="mt-1.5 h-11 rounded-2xl"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              New password
            </Label>
            <Input
              type="password"
              data-testid="admin-new-password-input"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              required
              minLength={6}
              className="mt-1.5 h-11 rounded-2xl"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Confirm new password
            </Label>
            <Input
              type="password"
              data-testid="admin-confirm-password-input"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              required
              minLength={6}
              className="mt-1.5 h-11 rounded-2xl"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-change-password-submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-sunset hover:bg-sunset-hover text-white font-bold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const StatCard = ({ label, value, icon, color, testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-3xl shadow-soft border border-slate-100 p-5 sm:p-6"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </span>
      <span
        className={`h-9 w-9 rounded-xl ${color} text-white flex items-center justify-center`}
      >
        {icon}
      </span>
    </div>
    <div className="mt-4 text-3xl sm:text-4xl font-heading font-black text-slate-900">
      {value}
    </div>
  </div>
);

const SlotManageCard = ({ slot, onBlock, onUnblock, onReserve }) => {
  let bg, text, border;
  if (slot.status === "available") {
    bg = "bg-status-available-bg/50";
    text = "text-emerald-800";
    border = "border-status-available/40";
  } else if (slot.status === "reserved") {
    bg = "bg-status-reserved-bg/60";
    text = "text-red-800";
    border = "border-status-reserved/30";
  } else {
    bg = "bg-status-blocked-bg";
    text = "text-slate-700";
    border = "border-status-blocked/40";
  }

  return (
    <div
      data-testid={`manage-slot-${slot.time}`}
      className={`rounded-2xl border-2 ${border} ${bg} p-3.5`}
    >
      <div className="flex items-baseline justify-between">
        <span className={`text-lg font-heading font-bold ${text}`}>
          {slot.time}
        </span>
        <span className="text-[10px] uppercase tracking-widest opacity-70">
          {slot.status}
        </span>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">→ {slot.end_time}</div>

      {slot.status === "reserved" && slot.customer_name && (
        <div
          className="mt-1.5 text-xs font-bold text-red-900 truncate"
          title={slot.customer_name}
        >
          {slot.customer_name}
        </div>
      )}
      {slot.status === "blocked" && slot.reason && (
        <div
          className="mt-1.5 text-xs font-bold text-slate-700 truncate"
          title={slot.reason}
        >
          {slot.reason}
        </div>
      )}

      <div className="mt-3 flex flex-col gap-1.5">
        {slot.status === "available" && (
          <>
            <Button
              size="sm"
              onClick={onReserve}
              data-testid={`reserve-${slot.time}`}
              className="rounded-full bg-ocean hover:bg-ocean-hover text-white h-8 text-xs"
            >
              <PlusCircle className="h-3 w-3 mr-1" /> Reserve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onBlock}
              data-testid={`block-${slot.time}`}
              className="rounded-full border-slate-300 h-8 text-xs"
            >
              <Ban className="h-3 w-3 mr-1" /> Block
            </Button>
          </>
        )}
        {slot.status === "blocked" && (
          <Button
            size="sm"
            onClick={onUnblock}
            data-testid={`unblock-${slot.time}`}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
          >
            Unblock
          </Button>
        )}
        {slot.status === "reserved" && (
          <span className="text-xs text-red-700 font-semibold text-center pt-1">
            Booked
          </span>
        )}
      </div>
    </div>
  );
};

const DeleteConfirm = ({ onConfirm, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        size="sm"
        variant="ghost"
        data-testid={testid}
        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent className="rounded-3xl">
      <AlertDialogHeader>
        <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-2">
          <ShieldAlert className="h-6 w-6 text-red-600" />
        </div>
        <AlertDialogTitle className="text-center">
          Delete this reservation?
        </AlertDialogTitle>
        <AlertDialogDescription className="text-center">
          This action cannot be undone. The slot will become available again.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="sm:justify-center">
        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          data-testid={`${testid}-confirm`}
          className="rounded-full bg-red-600 hover:bg-red-700"
        >
          Delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
